import {
  Component,
  OnInit,
  signal,
  inject,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  type Signal
} from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { isAuth } from './auth';
import { ThreeHtmlCanvasComponent } from './logomodel3d';

type ModalType = 'mapa' | 'streetview';

interface SocialLink {
  readonly label: string;
  readonly url: string;
  readonly iconClass: string;
  readonly color: string;
}

interface NavButton {
  readonly label: string;
  readonly route: string;
  readonly iconClass: string;
  readonly isDynamic?: boolean;
}

// Categorías para organizar en el acordeón
interface NavCategory {
  readonly title: string;
  readonly icon: string;
  readonly buttons: readonly NavButton[];
}

const NAV_CATEGORIES: readonly NavCategory[] = [
  {
    title: 'Académico e Institucional',
    icon: 'bi bi-mortarboard',
    buttons: [
      { label: 'Trámite', route: 'tramite', iconClass: 'bi bi-receipt' },
      { label: 'Artículos', route: 'articulo', iconClass: 'bi bi-journal-text' },
      { label: 'Estadísticas', route: 'stats', iconClass: 'bi bi-bar-chart-fill' },
      { label: 'Blog', route: 'blog', iconClass: 'bi bi-file-post' },
      { label: 'Cursos', route: 'curses', iconClass: 'bi bi-mortarboard-fill' },
      { label: 'Carreras', route: 'carreras', iconClass: 'bi bi-star' },
    ]
  },
  {
    title: 'Arte y Cultura',
    icon: 'bi bi-palette',
    buttons: [
      { label: 'Museo virtual', route: 'exhibicion', iconClass: 'bi bi-bank' },
      { label: 'Conferencia', route: 'video', iconClass: 'bi bi-camera-video-fill', isDynamic: true },
      { label: 'Modelo', route: 'model', iconClass: 'bi bi-journal-text' },
      { label: 'Sculpture', route: 'sculpt', iconClass: 'bi bi-bezier2' },
      { label: 'Pintura', route: 'pintura', iconClass: 'bi bi-palette' },
    ]
  },
  {
    title: 'Herramientas y Extras',
    icon: 'bi bi-tools',
    buttons: [
      { label: 'Game', route: 'game', iconClass: 'bi bi-controller' },
      { label: 'Audio', route: 'audio', iconClass: 'bi bi-badge-hd' },
    ]
  }
];

const SOCIAL_LINKS: readonly SocialLink[] = [
  { label: 'WhatsApp', url: 'https://api.whatsapp.com/send?phone=+51966999215&text=Hola%20bienvenido', iconClass: 'bi bi-whatsapp', color: '#069d12' },
  { label: 'Facebook', url: 'https://web.facebook.com/bellasartes.ayacucho', iconClass: 'bi bi-facebook', color: '#0672a0' },
  { label: 'Messenger', url: 'https://m.me/bellasartes.ayacucho', iconClass: 'bi bi-messenger', color: '#00B2FF' },
  { label: 'Instagram', url: 'https://instagram.com/bellasartes.ayacucho', iconClass: 'bi bi-instagram', color: '#ff00ee' },
  { label: 'Google / Blogspot', url: 'https://bellasartes.blogspot.com', iconClass: 'bi bi-google', color: '#ff4800' },
  { label: 'YouTube', url: 'https://www.youtube.com/channel/UCkYf1NyZ1kUl3965WgeL6rw', iconClass: 'bi bi-youtube', color: '#ff0011' },
  { label: 'TikTok', url: 'https://www.tiktok.com/@bellasartes.ayacucho', iconClass: 'bi bi-tiktok', color: '#efeaf3ff' }
];

@Component({
  selector: 'app-wwwwww',
  standalone: true,
  imports: [ThreeHtmlCanvasComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Modal genérico para Mapas / StreetView -->
    <dialog #mapDialog class="custom-native-modal shadow-lg" (click)="onDialogClick($event, mapDialog)">
      <div class="modal-content bg-dark text-light border-secondary h-100 d-flex flex-column">
        <div class="modal-header border-secondary p-3 d-flex justify-content-between align-items-center flex-shrink-0">
          <h5 class="modal-title mb-0">{{ modalTitle() }}</h5>
          <button type="button" class="btn-close btn-close-white" (click)="closeMapModal()"></button>
        </div>
        <div class="modal-body p-0 flex-grow-1 h-100 overflow-hidden">
          @if (currentIframeUrl(); as iframeUrl) {
            <iframe
              [src]="iframeUrl"
              [title]="modalTitle()"
              allowfullscreen
              loading="lazy"
              class="w-100 h-100 d-block border-0"
            ></iframe>
          }
        </div>
        <div class="modal-footer p-2 border-secondary d-flex justify-content-end">
          <button type="button" class="btn btn-secondary" (click)="closeMapModal()">Cerrar</button>
        </div>
      </div>
    </dialog>

    <!-- Modal con Acordeón para los Botones de Navegación -->
    <dialog #menuDialog class="custom-native-modal shadow-lg" (click)="onDialogClick($event, menuDialog)">
      <div class="modal-content bg-dark text-light border-secondary h-100 d-flex flex-column">
        <div class="modal-header border-secondary p-3 d-flex justify-content-between align-items-center flex-shrink-0">
          <h5 class="modal-title mb-0"><i class="bi bi-grid-fill me-2"></i> Menú de Navegación</h5>
          <!-- <button type="button" class="btn-close btn-close-white" (click)="closeMenuModal()"></button> -->
        </div>
        
        <div class="modal-body p-3 flex-grow-1 overflow-auto">
          <!-- Acordeón Bootstrap -->
          <div class="accordion accordion-flush" id="navAccordion">
            @for (cat of navCategories; track cat.title; let i = $index) {
              <div class="accordion-item bg-dark text-light border-secondary mb-2 rounded">
                <h2 class="accordion-header" [id]="'heading' + i">
                  <button 
                    class="accordion-button collapsed bg-secondary bg-opacity-10 text-light shadow-none" 
                    type="button" 
                    data-bs-toggle="collapse" 
                    [attr.data-bs-target]="'#collapse' + i" 
                    aria-expanded="false" 
                    [attr.aria-controls]="'collapse' + i"
                  >
                    <i [class]="cat.icon + ' me-2 text-info'"></i> {{ cat.title }}
                  </button>
                </h2>
                <div [id]="'collapse' + i" class="accordion-collapse collapse" [attr.aria-labelledby]="'heading' + i" data-bs-parent="#navAccordion">
                  <div class="accordion-body bg-dark d-flex flex-column gap-2">
                    @for (btn of cat.buttons; track btn.label) {
                      <button 
                        type="button"
                        class="btn btn-outline-light text-start d-flex align-items-center gap-2 py-2" 
                        (click)="navigateTo(btn)"
                      >
                        <i [class]="btn.iconClass + ' text-info'"></i>
                        <span>{{ btn.label }}</span>
                      </button>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        </div>

        <div class="modal-footer p-2 border-secondary d-flex justify-content-end">
          <button type="button" class="btn btn-secondary" (click)="closeMenuModal()">Cerrar</button>
        </div>
      </div>
    </dialog>

    <div class="container py-3">
      <!-- Información Institucional -->
      <div class="text-center">
        <h5 class="text-light my-0 ffont">
          ESCUELA SUPERIOR DE FORMACIÓN ARTÍSTICA
        </h5>
        <div class="text-light ffont">"FELIPE GUAMÁN POMA DE AYALA"</div>
        
        <!-- @if (auth()?.name; as name) {
          <div class="align-items-center gap-2 mt-2">
            <a routerLink="/actualizarusuario" class="btn btn-outline-info">
              <div class="text-light">Bienvenido: {{ name }}</div>
            </a>
          </div>
        } -->

        <div class="text-center" style="height: 7cm;">
          <app-three-html-canvas></app-three-html-canvas>
        </div>


        <!-- Botón único para desplegar el Modal con Acordeón de Navegación -->
        <div class="w-100 d-flex justify-content-center">
          <button 
            type="button"
            class="btn btn-outline-primary gap-1 d-flex align-items-center shadow" 
            (click)="openMenuModal()"
          >
            <i class="bi bi-list"></i>
            <span>MENÚ INSTITUCIONAL</span>
          </button>
        </div>
      </div>

      <!-- Dirección -->
      <div class="text-center my-2">
        <div class="mb-0 text-light style-location ffont">
          Jr. Mariano Melgar Nº 398, Distrito Jesús Nazareno - Huamanga, Ayacucho.
          Teléfono: 066-287499
        </div>
      </div>

      <!-- Redes Sociales y Modales de Mapas -->
      <div class="text-center my-2">
        <div class="d-flex justify-content-center flex-wrap gap-1">
          <button
            type="button"
            class="btn p-1"
            aria-label="Ver Street View"
            (click)="openMapModal('streetview')"
          >
            <i style="font-size: 21px; color: #15b5a7;" class="bi bi-person-walking"></i> 
          </button>
          <button
            type="button"
            class="btn p-1"
            aria-label="Ver Mapa"
            (click)="openMapModal('mapa')"
          >
            <i style="font-size: 21px; color: #1b39d4;" class="bi bi-geo-alt-fill"></i> 
          </button>

          @for (social of socialLinks; track social.label) {
            <button
              type="button"
              class="btn p-1"
              [attr.aria-label]="social.label"
              (click)="openInNewTab(social.url)"
            >
              <i
                [class]="social.iconClass"
                [style.color]="social.color"
                style="font-size: 21px;"
              ></i>
            </button>
          }
        </div>
<div class="text-muted small">
  Copyright &copy; ESFAPA Ayacucho {{ currentYear }}
</div>
      </div>
    </div>
  `,
  styles: [`
    
    .style-subtext {
      font-size: 0.75rem;
    }

    .style-location {
      font-size: 0.9rem;
    }

    .custom-native-modal {
      width: 90vw;
      height: 80vh;
      max-width: 600px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 12px;
      padding: 0;
      background: #0f172a;
      margin: auto;
    }

    .custom-native-modal::backdrop {
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(4px);
    }
  `]
})
export class WwwwwwComponent implements OnInit {
  @ViewChild('mapDialog') private readonly mapDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('menuDialog') private readonly menuDialog!: ElementRef<HTMLDialogElement>;
  
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);

  readonly currentYear = new Date().getFullYear();
  readonly imageSrc = signal<string>('');
  readonly auth: Signal<any> = signal(isAuth());

  readonly currentIframeUrl = signal<SafeResourceUrl | null>(null);
  readonly modalTitle = signal<string>('');

  readonly socialLinks = SOCIAL_LINKS;
  readonly navCategories = NAV_CATEGORIES;

  private readonly iframeSources: Record<ModalType, { title: string; url: SafeResourceUrl }> = {
    mapa: {
      title: 'Ubicación - ESFAPA Ayacucho',
      url: this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://maps.google.com/maps?width=100%25&height=500&hl=es&q=+(Escuela%20de%20Bellas%20Artes%20Felipe%20Guam%C3%A1n%20Poma%20de%20Ayala-Ayacucho)&t=k&z=18&ie=UTF8&iwloc=B&output=embed'
      )
    },
    streetview: {
      title: 'Street View - ESFAPA Ayacucho',
      url: this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://www.google.com/maps/embed?pb=!4v1750534065961!6m8!1m7!1syWFe4Rj4ga1j6fqlrkocMA!2m2!1d-13.15626194436401!2d-74.21787224051161!3f200.16657941141062!4f3.2355809602561294!5f0.7820865974627469'
      )
    }
  };

  ngOnInit(): void {
    const day = new Date().getDay();
    const svgIndex = day === 0 ? 7 : day;
    this.imageSrc.set(`/www${svgIndex}.svg`);
  }

  openMapModal(type: ModalType): void {
    const selectedSource = this.iframeSources[type];
    this.modalTitle.set(selectedSource.title);
    this.currentIframeUrl.set(selectedSource.url);
    
    if (this.mapDialog?.nativeElement) {
      this.mapDialog.nativeElement.showModal();
    }
  }

  closeMapModal(): void {
    if (this.mapDialog?.nativeElement?.open) {
      this.mapDialog.nativeElement.close();
    }
    this.currentIframeUrl.set(null);
  }

  openMenuModal(): void {
    if (this.menuDialog?.nativeElement) {
      this.menuDialog.nativeElement.showModal();
    }
  }

  closeMenuModal(): void {
    if (this.menuDialog?.nativeElement?.open) {
      this.menuDialog.nativeElement.close();
    }
  }

  onDialogClick(event: MouseEvent, dialogElement: HTMLDialogElement): void {
    const rect = dialogElement.getBoundingClientRect();
    const isClickOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (isClickOutside) {
      dialogElement.close();
      if (dialogElement === this.mapDialog?.nativeElement) {
        this.currentIframeUrl.set(null);
      }
    }
  }

  openInNewTab(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  navigateTo(btn: NavButton): void {
    this.closeMenuModal();
    if (btn.isDynamic) {
      const randomRoomId = Math.random().toString(36).substring(2, 11).toUpperCase();
      this.router.navigate([`/${btn.route}`, randomRoomId]);
      return;
    }
    this.router.navigate([btn.route]);
  }
}